import React, { useContext, useEffect, useReducer, useRef, useState } from "react";

import { PopupContentContext } from "../../../context/PopupContentContext";
import { Spinner } from "../../../useOss";
import Modal from "../../../useOss/Modal";
import { OrganizationMember, PendingMember, isCurrentMember } from "../../../dashboard/saas/Settings/types";
import inviteMembersToOrganization from "../../../api/organization/member/invite/post";
import { InputErrorMessage } from "../../../dashboard/saas/Settings/InputErrorMessage";
import { generateRandomId, isValidEmail } from "../../../dashboard/saas/utils";
import { cloneDeep } from "lodash";
import { OrganizationInfoResponse } from "../../../api/organization/get";
import { UserInfoResponse } from "../../../api/user/info";
import { sendOrganizationAnalytics } from "../../../utils";

type EmailState = {
    id: string;
    email: string;
    error: string | null;
};
type EmailAction =
    | { type: "Add_new_email" }
    | { type: "delete_email"; payload: { id: string } }
    | {
          type: "update_email_value";
          payload: { id: string; newValue: string; orgMembers: OrganizationMember[] };
      }
    | { type: "clear_state" }
    | { type: "update_user_already_in_other_org_error"; payload: { email: string } }
    | { type: "update_fields_error"; payload: { email: string; error: string }[] };

const initialInputState: Record<string, EmailState> = { 1: { id: "1", email: "", error: null } };

const errorsThatTriggerImmediateDelete = ["Invite already sent", "Duplicate email"];

function emailsReducer(emailState: Record<string, EmailState>, emailAction: EmailAction): Record<string, EmailState> {
    switch (emailAction.type) {
        case "Add_new_email": {
            const newId = generateRandomId();
            return {
                ...emailState,
                [newId]: {
                    id: newId,
                    email: "",
                    error: null
                }
            };
        }

        case "update_email_value": {
            const { id, newValue, orgMembers } = emailAction.payload;
            const trimmedNewValue = newValue.trim();

            const newEmailState: Record<string, EmailState> = {
                ...emailState,
                [id]: {
                    ...emailState[id],
                    email: newValue,
                    error: null
                }
            };

            const duplicateCounts: Record<string, number> = {};

            for (const { email } of Object.values(newEmailState)) {
                const trimmedEmail = email.trim();
                if (!trimmedEmail) continue;
                duplicateCounts[trimmedEmail] = (duplicateCounts[trimmedEmail] || 0) + 1;
            }

            for (const { id, email } of Object.values(newEmailState)) {
                if (duplicateCounts[email.trim()] === 1 && newEmailState[id].error === "Duplicate email") {
                    newEmailState[id].error = null;
                }
            }

            if (duplicateCounts[trimmedNewValue] > 1) {
                newEmailState[id].error = "Duplicate email";
            }

            if (!isValidEmail(trimmedNewValue)) {
                newEmailState[id].error = "Incorrect email format";
            }

            if (isValidEmail(trimmedNewValue) && newEmailState[id].error === "Incorrect email format") {
                newEmailState[id].error = null;
            }
            if (!trimmedNewValue) {
                newEmailState[id].error = null;
            }

            for (const orgMember of orgMembers) {
                if (orgMember.email === trimmedNewValue) {
                    newEmailState[id].error = isCurrentMember(orgMember)
                        ? "User is already a part of this organization"
                        : "Invite already sent";
                }
            }

            return newEmailState;
        }
        case "delete_email": {
            const { id } = emailAction.payload;
            const { [id]: deletedKey, ...rest } = emailState;
            return rest;
        }
        case "clear_state": {
            return initialInputState;
        }
        case "update_user_already_in_other_org_error": {
            const { email } = emailAction.payload;
            const errorObj: Record<string, true> = {};
            Object.values(emailState).forEach(emailValue => {
                if (email === emailValue.email) {
                    errorObj[emailValue.id] = true;
                }
            });
            return {
                ...emailState,
                ...Object.keys(errorObj).reduce((acc, curr) => {
                    return {
                        ...acc,
                        [curr]: { ...emailState[curr], error: "User associated with other organization" }
                    };
                }, {})
            };
        }
        case "update_fields_error": {
            const newState = cloneDeep(emailState);

            emailAction.payload.forEach(obj => {
                const fieldToUpdate = Object.values(emailState).find(({ email }) => email === obj.email);
                if (!fieldToUpdate) return;
                newState[fieldToUpdate.id] = {
                    ...newState[fieldToUpdate.id],
                    error: obj.error
                };
            });
            return newState;
        }
        default:
            return emailState;
    }
}

export function InviteMemberModal({
    isModalOpen,
    closeModal,
    orgMembers,
    onMembersInvited,
    maxNumberOfMembers,
    org,
    user
}: {
    isModalOpen: boolean;
    closeModal: () => void;
    orgMembers: OrganizationMember[];
    onMembersInvited: (pendingMembers: PendingMember[]) => void;
    maxNumberOfMembers: number;
    org: OrganizationInfoResponse;
    user: UserInfoResponse;
}) {
    const [state, setState] = useState<"IDLE" | "INVITING" | "COMPLETED" | "MEMBERS_FULL">(
        orgMembers.length === maxNumberOfMembers ? "MEMBERS_FULL" : "IDLE"
    );
    const [emails, updateEmails] = useReducer(emailsReducer, initialInputState);
    const [showAnnouncement, setShowAnnouncement] = useState(false);
    const { showToast } = useContext(PopupContentContext);
    const inputRef = useRef<HTMLInputElement>(null);

    const emailCount = Object.keys(emails).length;
    const emailValues = Object.values(emails);
    const isSingleEmail = emailCount === 1;

    const disableInviteButton =
        emailValues.some(email => !!email.error) || emailValues.every(({ email }) => !email.trim());
    async function handleInvitation(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault();
        const emailsToInvite = emailValues.filter(({ email }) => !!email).map(({ email }) => email);
        setState("INVITING");
        try {
            const response = await inviteMembersToOrganization(emailsToInvite);
            if (response.success) {
                setState("COMPLETED");
                onMembersInvited(
                    response.invitations.map(({ email, invitationLink }) => ({
                        email,
                        invitationLink,
                        role: "member",
                        invitationAccepted: false
                    }))
                );
                sendOrganizationAnalytics("members_invited", {
                    ...(org.organizationFound && {
                        orgId: org.organizationId,
                        author: { email: user.email, userId: user.userId, role: org.role },
                        numberOfInvitations: emailsToInvite.length
                    })
                });
            } else {
                const faultyFields = response.invitations.filter(invite => !!invite.issue) as {
                    email: string;
                    issue: string;
                }[];

                updateEmails({
                    type: "update_fields_error",
                    payload: faultyFields.map(invite => ({
                        error: invite.issue,
                        email: invite.email
                    }))
                });
                setState("IDLE");
            }
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Invitation Failed</>
            });
            setState("IDLE");
        }
    }
    function close() {
        closeModal();
        updateEmails({ type: "clear_state" });
        setState("IDLE");
    }

    function renderInviteButton() {
        switch (state) {
            case "IDLE":
                return (
                    <button
                        className="modal-button yellow-button"
                        style={{
                            width: "95px",
                            height: "37px"
                        }}
                        disabled={disableInviteButton}
                        onClick={handleInvitation}
                    >
                        Invite
                    </button>
                );
            case "INVITING":
                return (
                    <button
                        className="modal-button yellow-button loading"
                        disabled
                        style={{
                            color: "#FFFFFF",
                            backgroundColor: "#FF9933",
                            width: "95px",
                            height: "37px"
                        }}
                    >
                        <Spinner isInline />
                    </button>
                );
        }
    }

    function handleEmailValueChange(id: string, emailValue: string) {
        updateEmails({ type: "update_email_value", payload: { id, newValue: emailValue, orgMembers } });
    }

    function handleAddNewEmail() {
        if (orgMembers.length + emailCount === maxNumberOfMembers) {
            setShowAnnouncement(true);
            sendOrganizationAnalytics("maximum_number_of_invite_reached", {
                ...(org.organizationFound && {
                    orgId: org.organizationId,
                    author: { role: org.role, userId: user.userId, email: user.email }
                })
            });
            return;
        }
        updateEmails({ type: "Add_new_email" });
        setTimeout(() => {
            const inputElements = document.getElementsByName("invite-form-email-input");
            if (inputElements && inputElements.length > 0) {
                const lastInput = inputElements[inputElements.length - 1];
                lastInput.focus();
            }
        }, 0);
    }

    function renderSuccessView() {
        return (
            <div id="successful-invitation">
                <div id="invitation-heading" className="centered">
                    <img src="/static/assets/dashboard/success.png" />
                    Invite sent:
                </div>
                <div id="invitation-body">
                    {emailValues
                        .filter(({ email }) => !!email)
                        .map(({ email, id }) => (
                            <div id="invitation-email" key={id}>
                                {email}
                            </div>
                        ))}
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (isModalOpen) {
            inputRef && inputRef.current && inputRef.current.focus();
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (Object.keys(emails).length + orgMembers.length < maxNumberOfMembers) {
            showAnnouncement && setShowAnnouncement(false);
        }
    }, [emails]);

    switch (state) {
        case "MEMBERS_FULL":
            return (
                <Modal show={isModalOpen} modalClosed={() => close()} className={`${!isModalOpen ? "hide" : "mw-600"}`}>
                    <section className="modal-header-section centered">
                        <h4>Invite Member</h4>

                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => close()}
                        >
                            <path
                                d="M11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22Z"
                                fill="#E8E8E8"
                            />
                            <path
                                d="M7.2481 14.89C7.40424 15.0431 7.61474 15.1279 7.83337 15.1258C8.052 15.1238 8.26088 15.035 8.4141 14.879L11.0041 12.239L13.5951 14.879C13.6699 14.9606 13.7604 15.0264 13.8611 15.0723C13.9618 15.1182 14.0708 15.1433 14.1814 15.1463C14.2921 15.1492 14.4022 15.1298 14.5052 15.0893C14.6082 15.0488 14.7021 14.9879 14.7811 14.9104C14.8601 14.8329 14.9227 14.7402 14.9651 14.638C15.0076 14.5357 15.029 14.426 15.0282 14.3153C15.0274 14.2046 15.0043 14.0952 14.9603 13.9936C14.9163 13.8921 14.8523 13.8004 14.7721 13.724L12.1601 11.063L14.7721 8.40103C14.9167 8.24332 14.9943 8.03562 14.9887 7.82175C14.983 7.60787 14.8945 7.40456 14.7418 7.25471C14.5891 7.10486 14.3841 7.0202 14.1702 7.01858C13.9562 7.01696 13.7501 7.09851 13.5951 7.24603L11.0041 9.88603L8.4141 7.24603C8.3393 7.16443 8.24883 7.09872 8.14809 7.05281C8.04736 7.0069 7.93841 6.98173 7.82775 6.9788C7.71709 6.97588 7.60698 6.99526 7.50396 7.03578C7.40094 7.0763 7.30712 7.13714 7.22811 7.21467C7.1491 7.29221 7.0865 7.38485 7.04405 7.48709C7.00159 7.58932 6.98014 7.69907 6.98098 7.80977C6.98182 7.92046 7.00493 8.02986 7.04893 8.13144C7.09293 8.23302 7.15692 8.3247 7.2371 8.40103L9.8491 11.063L7.2371 13.724C7.08405 13.8802 6.99924 14.0907 7.00131 14.3093C7.00337 14.5279 7.09214 14.7368 7.2481 14.89Z"
                                fill="#6E6A65"
                            />
                        </svg>
                    </section>
                    <hr className="small-divider" />
                    <div id="team-announcement" style={{ margin: 0 }}>
                        <div>
                            To incorporate additional members into your team, please{" "}
                            <a
                                className="underline"
                                href="mailto:support@supertokens.com?subject=Request%20to%20incorporate%20additional%20members"
                                target="_blank"
                            >
                                contact support@supertokens.com
                            </a>
                            .
                        </div>
                    </div>
                    <hr className="small-divider" />
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button className="modal-button white-button" onClick={() => close()}>
                            Close
                        </button>
                    </div>
                </Modal>
            );
        case "IDLE":
        case "INVITING":
            return (
                <Modal show={isModalOpen} modalClosed={() => close()} className={`${!isModalOpen && "hide"}`}>
                    <section className="modal-header-section centered">
                        <h4>Invite Member</h4>

                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => close()}
                        >
                            <path
                                d="M11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22Z"
                                fill="#E8E8E8"
                            />
                            <path
                                d="M7.2481 14.89C7.40424 15.0431 7.61474 15.1279 7.83337 15.1258C8.052 15.1238 8.26088 15.035 8.4141 14.879L11.0041 12.239L13.5951 14.879C13.6699 14.9606 13.7604 15.0264 13.8611 15.0723C13.9618 15.1182 14.0708 15.1433 14.1814 15.1463C14.2921 15.1492 14.4022 15.1298 14.5052 15.0893C14.6082 15.0488 14.7021 14.9879 14.7811 14.9104C14.8601 14.8329 14.9227 14.7402 14.9651 14.638C15.0076 14.5357 15.029 14.426 15.0282 14.3153C15.0274 14.2046 15.0043 14.0952 14.9603 13.9936C14.9163 13.8921 14.8523 13.8004 14.7721 13.724L12.1601 11.063L14.7721 8.40103C14.9167 8.24332 14.9943 8.03562 14.9887 7.82175C14.983 7.60787 14.8945 7.40456 14.7418 7.25471C14.5891 7.10486 14.3841 7.0202 14.1702 7.01858C13.9562 7.01696 13.7501 7.09851 13.5951 7.24603L11.0041 9.88603L8.4141 7.24603C8.3393 7.16443 8.24883 7.09872 8.14809 7.05281C8.04736 7.0069 7.93841 6.98173 7.82775 6.9788C7.71709 6.97588 7.60698 6.99526 7.50396 7.03578C7.40094 7.0763 7.30712 7.13714 7.22811 7.21467C7.1491 7.29221 7.0865 7.38485 7.04405 7.48709C7.00159 7.58932 6.98014 7.69907 6.98098 7.80977C6.98182 7.92046 7.00493 8.02986 7.04893 8.13144C7.09293 8.23302 7.15692 8.3247 7.2371 8.40103L9.8491 11.063L7.2371 13.724C7.08405 13.8802 6.99924 14.0907 7.00131 14.3093C7.00337 14.5279 7.09214 14.7368 7.2481 14.89Z"
                                fill="#6E6A65"
                            />
                        </svg>
                    </section>
                    <hr />
                    <form id="invite-user-form">
                        <div id="invite-user-form-heading">Email</div>
                        {Object.values(emails).map(({ email, id, error }) => (
                            <div key={id}>
                                <div className="input-with-delete-container" style={{ marginTop: "12px" }}>
                                    <input
                                        type="text"
                                        value={email}
                                        onChange={e => {
                                            e.preventDefault();
                                            handleEmailValueChange(id, e.target.value);
                                        }}
                                        ref={inputRef}
                                        className={`${error && "error"}`}
                                        name="invite-form-email-input"
                                    />
                                    {!isSingleEmail && (
                                        <div
                                            id="delete-btn-container"
                                            className={`centered ${error &&
                                                errorsThatTriggerImmediateDelete.includes(error) &&
                                                "highlight"}`}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => updateEmails({ type: "delete_email", payload: { id } })}
                                        >
                                            <svg
                                                width="15"
                                                height="17"
                                                viewBox="0 0 15 17"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M12.0006 15.4542H2.99965V4.63614H1.50048V15.2223C1.49426 15.6567 1.64376 16.0783 1.92036 16.4063C2.19696 16.7343 2.58123 16.9457 2.99965 17H11.9994C12.418 16.9459 12.8026 16.7347 13.0794 16.4066C13.3563 16.0786 13.5059 15.6569 13.4998 15.2223V4.63614H12.0006V15.4542ZM9.75065 1.54579V0H5.25077V1.54579H0.00012207V3.09036H15.0001V1.54579H9.75065ZM5.25077 6.18193V13.9096H6.74995V6.18193H5.25077ZM8.2503 6.18193V13.9096H9.75065V6.18193H8.2503Z"
                                                    fill={`${
                                                        error && errorsThatTriggerImmediateDelete.includes(error)
                                                            ? "#ED344E"
                                                            : "#8D8D8D"
                                                    }`}
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                {<InputErrorMessage errorMessage={error} />}
                            </div>
                        ))}
                    </form>
                    {showAnnouncement && (
                        <div id="team-announcement">
                            <div>
                                To incorporate additional members into your team, please{" "}
                                <a
                                    className="underline"
                                    href="mailto:support@supertokens.com?subject=Request to incorporate additional members"
                                >
                                    contact support@supertokens.com
                                </a>
                                .
                            </div>
                        </div>
                    )}
                    <hr
                        className={`small-divider sm-hide ${!isSingleEmail &&
                            emailCount < maxNumberOfMembers &&
                            "mt-36"}`}
                    />
                    <div className="separated-flex" id="invite-member-btns-container">
                        <div>
                            <button
                                className="button-with-image large-padded-button centered outline-blue-button"
                                onClick={handleAddNewEmail}
                                disabled={showAnnouncement}
                            >
                                <span>&#43;</span> Add New
                            </button>
                        </div>
                        <hr
                            className={`small-divider sm-only ${!isSingleEmail &&
                                emailCount < maxNumberOfMembers &&
                                "mt-36"}`}
                        />

                        <div style={{ display: "flex", alignItems: "center" }}>
                            <button
                                className="modal-button white-button"
                                style={{
                                    marginRight: "24px"
                                }}
                                onClick={() => close()}
                            >
                                Cancel
                            </button>
                            {renderInviteButton()}
                        </div>
                    </div>
                </Modal>
            );
        case "COMPLETED":
            return (
                <Modal show={isModalOpen} modalClosed={() => close()} className={`${!isModalOpen && "hide"}`}>
                    <section className="modal-header-section centered">
                        <h4>Invite Member</h4>

                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => close()}
                        >
                            <path
                                d="M11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22Z"
                                fill="#E8E8E8"
                            />
                            <path
                                d="M7.2481 14.89C7.40424 15.0431 7.61474 15.1279 7.83337 15.1258C8.052 15.1238 8.26088 15.035 8.4141 14.879L11.0041 12.239L13.5951 14.879C13.6699 14.9606 13.7604 15.0264 13.8611 15.0723C13.9618 15.1182 14.0708 15.1433 14.1814 15.1463C14.2921 15.1492 14.4022 15.1298 14.5052 15.0893C14.6082 15.0488 14.7021 14.9879 14.7811 14.9104C14.8601 14.8329 14.9227 14.7402 14.9651 14.638C15.0076 14.5357 15.029 14.426 15.0282 14.3153C15.0274 14.2046 15.0043 14.0952 14.9603 13.9936C14.9163 13.8921 14.8523 13.8004 14.7721 13.724L12.1601 11.063L14.7721 8.40103C14.9167 8.24332 14.9943 8.03562 14.9887 7.82175C14.983 7.60787 14.8945 7.40456 14.7418 7.25471C14.5891 7.10486 14.3841 7.0202 14.1702 7.01858C13.9562 7.01696 13.7501 7.09851 13.5951 7.24603L11.0041 9.88603L8.4141 7.24603C8.3393 7.16443 8.24883 7.09872 8.14809 7.05281C8.04736 7.0069 7.93841 6.98173 7.82775 6.9788C7.71709 6.97588 7.60698 6.99526 7.50396 7.03578C7.40094 7.0763 7.30712 7.13714 7.22811 7.21467C7.1491 7.29221 7.0865 7.38485 7.04405 7.48709C7.00159 7.58932 6.98014 7.69907 6.98098 7.80977C6.98182 7.92046 7.00493 8.02986 7.04893 8.13144C7.09293 8.23302 7.15692 8.3247 7.2371 8.40103L9.8491 11.063L7.2371 13.724C7.08405 13.8802 6.99924 14.0907 7.00131 14.3093C7.00337 14.5279 7.09214 14.7368 7.2481 14.89Z"
                                fill="#6E6A65"
                            />
                        </svg>
                    </section>
                    <hr />
                    {renderSuccessView()}
                    <hr className="small-divider" />
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button className="modal-button white-button" onClick={() => close()}>
                            Close
                        </button>
                    </div>
                </Modal>
            );
    }
}
